import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { RealPropertyModal } from '../components/RealPropertyModal';
import { inventoryItemsApi } from '../inventoryApi';
import { ITEM_KIND, OWNERSHIP, OWNERSHIP_LABEL } from '../inventoryTypes';
import { realPropertiesApi } from '../realPropertyApi';
import type { RealPropertyListItem } from '../realPropertyTypes';

/**
 * The real-property register — one row per ACTUAL building or leased space.
 *
 * **It lists INSTANCES, never catalog types**, and the reference says why in its own words:
 * nobody opens a property register to read "Office Space × 3". The types are grouped on Stock
 * Levels' Real Property tab, which answers the other question — what KINDS of property are
 * held, and how many of each.
 *
 * **The custodian is absent from the search and the sort**, and that is the identity split
 * showing through rather than an omission: the name lives in `rsys_pii`, so sorting would mean
 * resolving every row before paging it and searching would mean copying names into `rsys_`. The
 * placeholder says what the search DOES cover.
 */
export function RealPropertiesIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RealPropertyListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    property: RealPropertyListItem;
  } | null>(null);

  const [working, setWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchProperties = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      realPropertiesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          tenure: query.facets.tenure?.join(','),
          category: query.facets.category?.join(','),
          type: query.facets.type?.join(','),
        },
        signal,
      ),
    [],
  );

  /*
    A property cannot be registered before a real-property TYPE exists, so the shell says so
    before the reader opens a form they could not submit. Seeded MET and left met on a failed
    feed: a request that did not come back is not evidence that a catalog is empty.
  */
  const [hasType, setHasType] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    inventoryItemsApi
      .options(controller.signal)
      .then((options) => {
        if (controller.signal.aborted) return;
        setHasType(options.some((i) => i.kind === ITEM_KIND.realProperty));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const rowActions = useCallback(
    (property: RealPropertyListItem): MenuItem[] =>
      property.isActive
        ? []
        : [
            {
              key: 'restore',
              label: 'Restore',
              icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
              permission: 'real-properties.delete',
              onClick: () => setConfirming({ kind: 'activate', property }),
            },
          ],
    [],
  );

  async function run() {
    if (!confirming) return;

    setWorking(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await realPropertiesApi.deactivate(confirming.property.id);
      } else {
        await realPropertiesApi.activate(confirming.property.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('That could not be completed.', 0),
      );
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <IndexPage<RealPropertyListItem>
        module="real-properties"
        breadcrumb={[
          'Logistics',
          'Inventory',
          /* The breadcrumb IS the back-nav, and a page one level inside another has no other
             way out — so Settings links, per every settings-nested module here. */
          { label: 'Settings', to: '/inventory/settings' },
          'Real Properties',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'tenure', label: 'Tenure' },
          { key: 'type', label: 'Type', permission: 'inventory-items.read' },
          { key: 'category', label: 'Category', permission: 'inventory-items.read' },
        ]}
        prerequisites={[
          {
            label: 'a real-property type',
            met: hasType,
            to: '/inventory/settings/items',
            permission: 'inventory-items.read',
          },
        ]}
        auditEntityTypes={['RealProperty']}
        rowKey={(property) => property.id}
        fetch={fetchProperties}
        reloadKey={reloadKey}
        // The custodian is deliberately NOT named here — see the remarks on this page.
        searchPlaceholder="Search code, name, type or location"
        emptyMessage="No properties registered yet. Register one under a real-property type."
        onCreate={() => setCreating(true)}
        createPermission="real-properties.create"
        onRowOpen={(property) => navigate(`/inventory/settings/real-properties/${property.id}`)}
        openPermission="real-properties.read"
        onEdit={(property) => setEditing(property)}
        editPermission="real-properties.update"
        onDelete={(property) => setConfirming({ kind: 'deactivate', property })}
        deletePermission="real-properties.delete"
        rowActions={rowActions}
        otherInfo={(property) => ({
          createdAt: property.createdAt,
          createdByName: property.createdByName,
          updatedAt: property.updatedAt,
          updatedByName: property.updatedByName,
        })}
      />

      {creating || editing ? (
        <RealPropertyModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          id="real-property-confirm-dialog"
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void run()}
          title={confirming.kind === 'deactivate' ? 'Delete property' : 'Restore property'}
          danger={confirming.kind === 'deactivate'}
          /*
            DELETE takes both safeguards; RESTORE takes neither — putting something back is an
            undo, and ceremony on an undo just trains people to click through ceremony.
          */
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={working}
          body={
            <div className="flex flex-col gap-3">
              {confirming.kind === 'deactivate' ? (
                <>
                  <span className="font-medium">{confirming.property.name}</span> is kept and can be
                  restored — nothing is destroyed. It leaves the register and its code{' '}
                  <span className="font-mono">{confirming.property.propertyCode}</span> becomes free
                  for another property to take, so restoring it later may need a new one.
                </>
              ) : (
                <>
                  <span className="font-medium">{confirming.property.name}</span> returns to the
                  register. Its code must still be free — if another property took{' '}
                  <span className="font-mono">{confirming.property.propertyCode}</span> in the
                  meantime this is refused, and that one has to be changed first.
                </>
              )}

              {/* A restore can be REFUSED — the code was freed and something live may hold it
                  now — so the failure has to land where the reader is looking. */}
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<RealPropertyListItem>[] = [
  {
    /*
      The ANCHOR carries TWO values on one line — the name and the code — which is the one place
      "one column, one value" yields. Neither identifies the row alone: a register holds three
      "Annex Building" rows across three campuses, and a column of PROP-0042 titles every row
      identically to anyone who has not memorised the codes.

      A `<StackedCell>` rather than a merge, because this page uses the SHARED index table, where
      a stacked secondary is dev-errored. So the two are joined into one string instead.
    */
    key: 'name',
    header: 'Property',
    width: 260,
    sortable: true,
    render: (property) => (
      <StackedCell primary={property.name} secondary={property.propertyCode} />
    ),
  },
  {
    key: 'propertyCode',
    header: 'Code',
    width: 140,
    sortable: true,
    render: (property) => <TextCell value={property.propertyCode} mono />,
  },
  {
    key: 'typeName',
    header: 'Type',
    width: 170,
    sortable: true,
    summary: true,
    render: (property) => <TextCell value={property.typeName} />,
  },
  {
    key: 'ownership',
    header: 'Tenure',
    width: 110,
    sortable: true,
    summary: true,
    render: (property) => <TextCell value={OWNERSHIP_LABEL[property.ownership]} />,
  },
  {
    key: 'locationLabel',
    header: 'Location',
    width: 260,
    summary: true,
    // Composed on the SERVER as a get-only property over the four address parts, so it cannot
    // disagree with them. Never re-joined here.
    render: (property) => <TextCell value={property.locationLabel} />,
  },
  {
    key: 'floorAreaSqm',
    header: 'Area (sqm)',
    width: 120,
    align: 'right',
    sortable: true,
    // `numeric` mutes a zero — though zero is refused here, so what it really mutes is nothing.
    // It is on for the column's own sake: a quantity column reads as one.
    render: (property) => (
      <TextCell
        value={property.floorAreaSqm === null ? null : String(property.floorAreaSqm)}
        mono
        numeric
      />
    ),
  },
  {
    /*
      ONE column for two figures, because they are the same question asked of the two tenures —
      what this property costs. The header says both, and only one can ever be set: the server
      clears whichever the tenure does not ask for.
    */
    key: 'value',
    header: 'Value / rent',
    width: 150,
    align: 'right',
    render: (property) => {
      const amount =
        property.ownership === OWNERSHIP.leased
          ? property.monthlyRent
          : property.acquisitionCost;

      return <TextCell value={amount === null ? null : formatMoney(amount)} mono numeric />;
    },
  },
  {
    key: 'leaseEndDate',
    header: 'Lease ends',
    width: 140,
    sortable: true,
    /*
      A calendar date, so `formatDateOnly` through `<TextCell mono />` rather than `<DateCell>`:
      a lease ends on a DAY, and the instant path would tell a reader west of UTC it ended the
      day before.

      An em dash on an owned property is the honest answer — it has no lease to end, which is a
      different fact from a lease with no date.
    */
    render: (property) => (
      <TextCell
        value={property.leaseEndDate === null ? null : formatDateOnly(property.leaseEndDate)}
        mono
      />
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    /*
      TWO states in one column, deliberately: the row's own live/deleted marker, and — for a live
      leased property — whether its lease has lapsed. A lapsed lease is the thing this register
      is scanned for, and it has nowhere else to show.
    */
    render: (property) =>
      !property.isActive ? (
        <StatusText tone="inactive">deleted</StatusText>
      ) : property.isLeaseExpired ? (
        <StatusText tone="blocked">lease expired</StatusText>
      ) : (
        <StatusText tone="active">active</StatusText>
      ),
  },
];
